import React from 'react';
import { Link } from 'react-router-dom';
import { FilterOutlined } from '@ant-design/icons';
import styled, { css } from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import {
  INDUSTRY_DISPLAY,
  IndustrySlug
} from 'containers/Setting/components/RoleFormModal/customerIntelligence';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import usePerx3Redesign from 'hooks/usePerx3Redesign';

import CardFreshnessStamp from '../CardFreshnessStamp';

/* Inherits the title's ink and weight so the heading reads exactly as it did; the underline on
   hover and focus is the whole of the added affordance. antd's global `a` rule is a single element
   selector, so this class outranks it. */
const TitleLink = styled(Link)`
  color: inherit;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    color: inherit;
    text-decoration: underline;
  }
`;

export interface IPageHeaderProps {
  /** Page name; the suite prefix is composed here so the separator stays translatable. */
  title: string;
  /** Tenant industry. Read-only on this page — it is set in Settings, never picked here. */
  industry: IndustrySlug | null;
  /**
   * Render `title` as given instead of through the `Intelligence Suite/ {{page}}` template. Customer
   * 360 draws its heading as `Customer 360 / <id>` with no suite prefix, matching the customer
   * detail page beside it. PLA-8844.
   */
  rawTitle?: boolean;
  /**
   * Links the leading `label` of the drawn heading at `to`. The label is passed rather than sliced
   * off at the separator so a translated heading that no longer opens with it renders plain instead
   * of linking the wrong words. PLA-8844.
   */
  titleLink?: { label: string; to: string };
  /**
   * The tenant config has not arrived yet, so `industry` is unknown rather than absent. An
   * unresolved industry must not be reported as a misconfiguration.
   */
  industryPending?: boolean;
  /** ISO 8601 instant of the page-level refresh; per-card freshness stays on each card. */
  updatedAt?: string | null;
  onToggleFilters?: () => void;
  filtersActive?: boolean;
  /**
   * The filter bar. It renders inside the header, above the rule, so the hairline separates the
   * whole header block — title and filters together — from the bands rather than cutting between
   * the two halves of it.
   */
  filters?: React.ReactNode;
  className?: string;
}

const FILTER_ICON_SIZE = 24;

const PageHeaderWrapper = styled.header`
  display: flex;
  flex-direction: column;
  padding-bottom: ${tokens.space['16']};
  border-bottom: 1px solid ${tokens.color['border-subtle']};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

// Figma seats the first chip row 16px under the title block's line box (node 589-33313: the industry
// line's box bottom is y=440, the first chip top y=456). The bar contributes nothing of its own on
// either side, so this wrapper is the only vertical space in the seam.
const FilterSlot = styled.div`
  margin-top: ${tokens.space['16']};
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). The doubled class
     outranks the children's own margin reset, which is injected later and would otherwise win the
     cascade on equal specificity and collapse this gap to nothing. */
  && > * + * {
    margin-top: ${tokens.space['4']};
  }
`;

const Title = styled.h1`
  margin: 0;
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['display-size']};
  font-weight: ${tokens.type['display-weight']};
  line-height: ${tokens.type['display-line']};
`;

/**
 * The breadcrumb with the industry stacked under it (flag ON). Flag OFF renders the same two nodes
 * as direct children of `TitleBlock` - so this wrapper exists only under the flag and
 * `TitleBlock`'s own `* + *` gap keeps counting the children it counted before.
 */
const BreadcrumbRow = styled.div`
  display: flex;
  flex-direction: column;

  /* The pill hugs its own text; stretching it to the title's width would read as a banner. */
  align-items: flex-start;

  /* The h1 carries no margin of its own (Title resets it), and the row states it too so a title
     that gains one cannot widen the seam under it. */
  > h1 {
    margin: 0;
  }

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: ${tokens.space['8']};
  }
`;

/* `align-self` and the zero margin are stated on the pill itself, not left to the row's
   `align-items`, so a UA `p` margin reaching it through a later stylesheet cannot pull it off the
   title's left edge with the row's own rule still satisfied. */
const industryPillRules = css`
  align-self: flex-start;
  margin: 0;
  padding: 2px 10px;
  border: 1px solid ${tokens.color['industry-pill-border']};
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
`;

/* The page subtitle role of the design system's type scale (PLA-8844). */
const Industry = styled.p<{ $perx3: boolean }>`
  margin: 0;
  background: ${tokens.color['industry-pill-bg']};
  color: ${tokens.color['industry-pill-ink']};
  font-size: ${tokens.type['page-subtitle-size']};
  font-weight: ${tokens.type['page-subtitle-weight']};
  line-height: ${tokens.type['page-subtitle-line']};

  /* The pill is geometry only. Both colours come from tokens in either variant, whose dark readings
     are what this line already drew - no fill, and the positive ink it carried. */
  ${({ $perx3 }) => ($perx3 ? industryPillRules : null)}

  /* Last, so it still outranks the pill ink for a tenant with no industry configured. */
  &[data-industry='none'] {
    color: ${tokens.color['text-muted']};
  }
`;

const IndustryNote = styled.p`
  margin: 0;
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
`;

const Controls = styled.div`
  display: flex;
  align-items: center;

  > * + * {
    margin-left: ${tokens.space['16']};
  }
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  padding: 0;
  border: none;
  border-radius: ${tokens.radius.xs};
  background: none;
  color: ${tokens.color['text-primary']};
  cursor: pointer;

  .anticon {
    font-size: ${FILTER_ICON_SIZE}px;
  }

  &[data-active='true'] {
    color: ${tokens.color['accent-blue']};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.elevation['focus-ring']};
  }
`;

const PageHeader: React.FC<IPageHeaderProps> = ({
  title,
  industry,
  industryPending = false,
  rawTitle = false,
  titleLink,
  updatedAt,
  onToggleFilters,
  filtersActive = false,
  filters,
  className
}) => {
  const { t } = useMultiLanguageTranslation();
  const isPerx3 = usePerx3Redesign();

  // The slug reaches this page from unvalidated tenant config, so an industry the permission
  // catalog does not know reads as "not configured" rather than printing a raw slug.
  const industryName =
    industry && Object.prototype.hasOwnProperty.call(INDUSTRY_DISPLAY, industry)
      ? INDUSTRY_DISPLAY[industry]
      : null;

  const heading = rawTitle ? title : t(`${TCustomerInsights}.Header.Title`, { page: title });
  const linked = titleLink && heading.startsWith(titleLink.label) ? titleLink : null;

  const titleNode = (
    <Title data-testid="page-header-title">
      {linked ? (
        <>
          <TitleLink to={linked.to} data-testid="page-header-title-link">
            {linked.label}
          </TitleLink>
          {heading.slice(linked.label.length)}
        </>
      ) : (
        heading
      )}
    </Title>
  );

  /* While the tenant config is in flight the header makes no claim about the industry at all:
     "not configured" is an assertion about a resolved value, and a correctly configured tenant
     would otherwise be told its industry is missing on every load. */
  const industryNode = industryPending ? null : (
    <Industry
      $perx3={isPerx3}
      data-testid="page-header-industry"
      data-industry={industryName ? industry : 'none'}
    >
      {industryName
        ? t(`${TCustomerInsights}.Header.Industry`, { industry: industryName })
        : t(`${TCustomerInsights}.Header.IndustryNotConfigured`)}
    </Industry>
  );

  /* Flag ON seats the industry pill beside the breadcrumb; OFF keeps both as direct children of
     `TitleBlock`, i.e. the exact tree this header rendered before the flag existed. */
  const breadcrumb = isPerx3 ? (
    <BreadcrumbRow data-testid="page-header-breadcrumb-row">
      {titleNode}
      {industryNode}
    </BreadcrumbRow>
  ) : (
    <>
      {titleNode}
      {industryNode}
    </>
  );

  return (
    <PageHeaderWrapper className={className} data-testid="page-header">
      <TitleRow>
        <TitleBlock>
          {breadcrumb}
          {industryName || industryPending ? null : (
            <IndustryNote data-testid="page-header-industry-note">
              {t(`${TCustomerInsights}.Header.IndustryNotConfiguredNote`)}
            </IndustryNote>
          )}
        </TitleBlock>
        <Controls>
          {updatedAt ? <CardFreshnessStamp calculatedAt={updatedAt} /> : null}
          {onToggleFilters ? (
            <FilterButton
              type="button"
              onClick={onToggleFilters}
              aria-expanded={filtersActive}
              aria-label={t(`${TCustomerInsights}.Header.ToggleFilters`)}
              data-active={filtersActive}
              data-testid="page-header-filters"
            >
              <FilterOutlined aria-hidden />
            </FilterButton>
          ) : null}
        </Controls>
      </TitleRow>
      {filters ? <FilterSlot data-testid="page-header-filter-slot">{filters}</FilterSlot> : null}
    </PageHeaderWrapper>
  );
};

export default PageHeader;
